import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "@lingui/core/macro";
import { i18n } from "@app/i18n";
import { apiClient } from "@/lib/ApiClient";

export const onboardingKeys = {
  all: ["onboarding"] as const,
  status: () => ["onboarding", "status"] as const,
};

export type OnboardingCompleteInput = {
  referralSource: string;
  role: string;
  teamName: string;
};

/** GET /api/onboarding 로 세션 유저의 온보딩 완료 여부를 조회한다. */
export function useOnboardingStatusQuery(enabled: boolean) {
  return useQuery({
    queryKey: onboardingKeys.status(),
    queryFn: async () => {
      const res = await apiClient.api.onboarding.$get();
      // 응답이 실패했는지
      const isResNotOk = !res.ok;
      if (isResNotOk) throw new Error("failed to load onboarding status");
      return res.json();
    },
    enabled,
  });
}

/** POST /api/onboarding/complete 로 온보딩을 완료하고 첫 팀을 생성한다. */
export function useCompleteOnboardingMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: OnboardingCompleteInput) => {
      const res = await apiClient.api.onboarding.complete.$post({ json: values });
      // 응답이 실패했는지
      const isResNotOk = !res.ok;
      if (isResNotOk) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? t(i18n)`온보딩을 완료하지 못했습니다.`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.setQueryData(onboardingKeys.status(), { completed: true });
    },
  });
}

export function useOnboardingService() {
  return useCompleteOnboardingMutation();
}
